/** The one `<style>` injector in this package, and the cascade rule it keeps.
 *
 * Until 2026-09-17 twenty files each carried the same eleven lines: bump a
 * module-level count, find-or-create a `<style id>` in `<head>`, drop it when
 * the count returns to zero. Every copy agreed on those semantics, so this is
 * that function once. Every sheet keeps the id it always had.
 *
 * ## The cascade rule
 *
 * Where two sheets declare one selector at equal specificity the later one in
 * `<head>` wins, and "later" used to mean whichever effect React ran first.
 * A child's effect runs before its parent's, so in the live app the screens'
 * sheet landed before the kit's, and the polish sheet landed after the
 * new-game screen's own sheet but before the main menu's (measured with a
 * playwright probe over the debug scenes, 2026-09-17). The comments that said
 * "always after" were true only for the scene their author opened.
 *
 * Every sheet now names a LAYER, and the injector keeps the sheets it manages
 * in `<head>` in non-decreasing layer order whatever the mount order: a new
 * sheet goes before the first managed sheet with a higher layer, or at the
 * end of `<head>` when there is none. Within one layer mount order still
 * decides, which is what the screens' private sheets always had. Elements this
 * module did not create - vite's own CSS, a qa `--inject` tag - are never
 * moved. The layers, lowest first:
 *
 *   theme      the kit, `THEME_CSS`
 *   adventure  the adventure shell's material
 *   screens    `SCREENS_CSS`, shared by every full-screen panel
 *   polish     `POLISH_CSS`, which beats `screens` at equal specificity
 *   screen     one screen's own sheet, which beats both
 *   overlay    a sheet drawn over a screen's own: the town painting over the
 *              town chrome
 */

import { useEffect } from "react";

export const StyleLayer = Object.freeze({
  theme: 0,
  adventure: 10,
  screens: 20,
  polish: 30,
  screen: 40,
  overlay: 50,
});
export type StyleLayer = (typeof StyleLayer)[keyof typeof StyleLayer];

/** Marks a sheet this module placed, and carries the layer it was placed at. */
export const STYLE_LAYER_ATTR = "data-hm-layer";

const refs = new Map<string, number>();

function place(el: HTMLStyleElement, layer: number): void {
  const managed = document.head.querySelectorAll<HTMLStyleElement>(`style[${STYLE_LAYER_ATTR}]`);
  for (let i = 0; i < managed.length; i++) {
    const other = managed[i]!;
    if (Number(other.getAttribute(STYLE_LAYER_ATTR)) > layer) {
      document.head.insertBefore(el, other);
      return;
    }
  }
  document.head.appendChild(el);
}

/**
 * Mount `css` under `id` at `layer`, refcounted; returns the release. The
 * sheet is created on the first acquire and removed when the last release
 * lands, so a test that mounts and unmounts a screen leaves the document
 * clean. An element that already carries the id is adopted as-is, text and
 * position included, which is what every copy of this did.
 */
export function installStyleSheet(id: string, css: string, layer: StyleLayer): () => void {
  refs.set(id, (refs.get(id) ?? 0) + 1);
  let el: HTMLElement | null = document.getElementById(id);
  if (el === null) {
    const made = document.createElement("style");
    made.id = id;
    made.setAttribute(STYLE_LAYER_ATTR, String(layer));
    made.textContent = css;
    place(made, layer);
    el = made;
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const left = (refs.get(id) ?? 1) - 1;
    refs.set(id, left);
    if (left === 0) el?.remove();
  };
}

/** `installStyleSheet` for the life of the calling component. */
export function useStyleSheet(id: string, css: string, layer: StyleLayer): void {
  useEffect(() => installStyleSheet(id, css, layer), [id, css, layer]);
}
